(() => {
  const formatMoney = (value) => {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(value);
    }

    return (Number(value) / 100).toFixed(2);
  };

  class UpsellVariantCard {
    constructor(card) {
      this.card = card;
      this.variants = this.parseVariants(card.dataset.variants);
      this.recurringPlanIds = this.parseRecurringPlanIds(card.dataset.recurringPlanIds);
      // Subscription mode: always add on the first recurring plan and price the card at it.
      this.subscribe = card.dataset.subscribe === 'true';
      this.checkbox = card.querySelector('.upsell-card__checkbox');
      this.sellingPlanInput = card.querySelector('.upsell-card__selling-plan');
      this.priceEl = card.querySelector('[data-upsell-price]');
      this.compareEl = card.querySelector('[data-upsell-compare-at]');
      this.discountEls = card.querySelectorAll('[data-upsell-discount]');
      // Locale-specific badge, e.g. "25%" or "25 %" in German.
      this.badgeFormat = card.closest('[data-badge-format]')?.dataset.badgeFormat || '[percent]%';
      this.optionInputs = card.querySelectorAll('input.bundle-option');
      this.imageEl = card.querySelector('[data-upsell-image]');
      this.currentVariant = null;

      this.attachEvents();
      this.applyVariant(this.getCurrentVariant());
      this.toggleSellingPlan();
    }

    parseVariants(data) {
      try {
        return JSON.parse(data) || [];
      } catch (err) {
        console.warn('Upsell variants: unable to parse variants json', err);
        return [];
      }
    }

    parseRecurringPlanIds(data) {
      return (data || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    attachEvents() {
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange.bind(this));
      });

      this.checkbox?.addEventListener('change', () => this.toggleSellingPlan());
    }

    getSelectedOptions() {
      const selected = {};
      this.optionInputs.forEach((input) => {
        if (input.checked) {
          const position = input.dataset.optionPosition;
          selected[`option${position}`] = input.value;
        }
      });

      return selected;
    }

    getCurrentVariant() {
      if (!this.variants.length) return null;

      const selected = this.getSelectedOptions();
      const hasSelections = Object.keys(selected).length > 0;

      if (!hasSelections) {
        return this.variants.find((variant) => variant.available) || this.variants[0];
      }

      return this.variants.find(
        (variant) =>
          (!selected.option1 || variant.option1 === selected.option1) &&
          (!selected.option2 || variant.option2 === selected.option2) &&
          (!selected.option3 || variant.option3 === selected.option3),
      ) || null;
    }

    onOptionChange() {
      this.applyVariant(this.getCurrentVariant());

      console.log("event.target.dataset.imageSrc", event.target.dataset.imageSrc);

      if(!event.target.dataset.imageSrc) return

      this.updateImage({featured_image: {src: event.target.dataset.imageSrc}})
    }

    applyVariant(variant) {
      this.currentVariant = variant;

      if (!variant) {
        this.disableCard();
        return;
      }

      if (this.checkbox) {
        this.checkbox.value = variant.id;
        this.checkbox.disabled = !variant.available;

        if (!variant.available) {
          this.checkbox.checked = false;
        }
      }

      this.updatePrices(variant);
      this.updateImage(variant);
      this.updateSellingPlanValue(variant);
      this.toggleSellingPlan();
    }

    disableCard() {
      if (this.checkbox) {
        this.checkbox.checked = false;
        this.checkbox.disabled = true;
      }

      if (this.priceEl) {
        this.priceEl.textContent = theme?.strings?.noVariant || '';
      }

      if (this.compareEl) {
        this.compareEl.textContent = '';
      }

      this.updateSellingPlanValue(null);
      this.updateDiscount(0);
      this.toggleSellingPlan();
    }

    getSubscriptionAllocation(variant) {
      const allocations = variant?.selling_plan_allocations || [];
      for (const planId of this.recurringPlanIds) {
        const allocation = allocations.find((item) => String(item.selling_plan_id) === planId);
        if (allocation) return allocation;
      }
      return null;
    }

    updatePrices(variant) {
      let price = variant.price || 0;
      let compareAt = variant.compare_at_price || 0;

      const allocation = this.subscribe ? this.getSubscriptionAllocation(variant) : null;
      if (allocation) {
        price = allocation.price;
        compareAt = allocation.compare_at_price || variant.price;
      }

      if (this.priceEl) {
        this.priceEl.innerHTML = formatMoney(price);
      }

      if (this.compareEl) {
        this.compareEl.innerHTML = compareAt > price ? formatMoney(compareAt) : '';
      }

      const percent = compareAt > price ? Math.round(((compareAt - price) * 100) / compareAt) : 0;

      this.updateDiscount(percent);
    }

    updateDiscount(percent) {
      this.discountEls.forEach((el) => {
        el.textContent = percent > 0 ? this.badgeFormat.replace('[percent]', percent) : '';
      });
    }

    updateImage(variant) {
      if (!this.imageEl) return;

      const newSrc = variant?.featured_image?.src || variant?.image?.src;
      if (newSrc) {
        this.imageEl.src = newSrc;
        this.imageEl.srcset = '';
      }
    }

    updateSellingPlanValue(variant) {
      if (!this.sellingPlanInput) return;

      const planId = this.getRecurringPlanId(variant);
      this.sellingPlanInput.value = planId;
      if (!planId) {
        this.sellingPlanInput.disabled = true;
      }
    }

    getRecurringPlanId(variant) {
      if (this.subscribe) {
        const allocation = this.getSubscriptionAllocation(variant);
        return allocation ? String(allocation.selling_plan_id) : '';
      }

      const allocations = variant?.selling_plan_allocations || [];
      if (!allocations.length) return '';

      // Only ever attach a recurring plan. Fixed (non-recurring) twins exist for the
      // iDEAL workaround and would turn the order into a one-time purchase.
      const recurring = allocations.find((allocation) =>
        this.recurringPlanIds.includes(String(allocation.selling_plan_id)),
      );

      return recurring ? String(recurring.selling_plan_id) : '';
    }

    toggleSellingPlan() {
      if (!this.sellingPlanInput) return;

      const shouldEnable =
        this.checkbox?.checked &&
        (this.subscribe || this.currentVariant?.requires_selling_plan) &&
        this.sellingPlanInput.value !== '';

      this.sellingPlanInput.disabled = !shouldEnable;
    }
  }

  function initUpsellVariantCards() {
    const upsellCards = document.querySelectorAll('.upsells-multiselect--variants [data-upsell-card]');
    upsellCards.forEach((card) => new UpsellVariantCard(card));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellVariantCards);
  } else {
    initUpsellVariantCards();
  }
})();
